import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { IFavouriteItemFeature } from './favourite-item.feature.state';
import {
  getFailures,
  getFavoriteItem,
  getFavouriteLocations,
  getFavouriteVouchers,
  getFavouriteWallets,
  getIsLoading,
  selectFavoriteItemsByType,
  selectLimitReachedByType,
} from './favourite-item.selectors';
import {
  AddFavouriteItemAction,
  ClearFavoriteItemsAction,
  ClearStateAction,
  GetFavouriteLocationsAction,
  GetFavouriteVouchersAction,
  GetFavouriteWalletsAction,
  RemoveFavouriteItemAction,
  setAccountID,
} from './favourite-item.actions';
import { combineLatest, Observable, of } from 'rxjs';
import { catchError, filter, map } from 'rxjs/operators';
import { getItemId } from '../utils';

@Injectable()
export class FavouriteItemFacade {
  public isLoading$ = this.store.select(getIsLoading);
  public failures$ = this.store.select(getFailures);
  public favouriteItem$ = this.store.select(getFavoriteItem);

  public getFavouriteLocations$ = this.store.select(getFavouriteLocations).pipe(filter((item) => !!item));
  public getFavouriteVouchers$ = this.store.select(getFavouriteVouchers).pipe(filter((item) => !!item));
  public getFavouriteWallets$ = this.store.select(getFavouriteWallets).pipe(filter((item) => !!item));

  public favouriteLocationIds$ = this.favouriteItem$.pipe(
    map((favouriteItem) => {
      if (favouriteItem && favouriteItem.location) {
        return new Set((favouriteItem.location || []).map((item) => item.id));
      }
      return new Set(); // Emit an empty Set instead of EMPTY
    })
  );

  public favouriteVoucherIds$ = this.favouriteItem$.pipe(
    map((favouriteItem) => {
      if (favouriteItem && favouriteItem.voucher) {
        return new Set((favouriteItem.voucher || []).map((item) => getItemId('voucher', item)));
      }
      return new Set(); // Emit an empty Set instead of EMPTY
    })
  );
  public favouriteWalletIds$ = this.favouriteItem$.pipe(
    map((favouriteItem) => {
      if (favouriteItem && favouriteItem.wallet) {
        return new Set((favouriteItem.wallet || []).map((item) => getItemId('wallet', item)));
      }
      return new Set(); // Emit an empty Set instead of EMPTY
    })
  );

  public favouriteLocations$ = this.favouriteItem$.pipe(map((favouriteItem) => favouriteItem.location));
  public favouriteVouchers$ = this.favouriteItem$.pipe(map((favouriteItem) => favouriteItem.voucher));
  public favouriteWallets$ = this.favouriteItem$.pipe(map((favouriteItem) => favouriteItem.wallet));

  constructor(private store: Store<IFavouriteItemFeature>) {}

  // public getFavouriteItem(params: RequestFavouriteItemParams): void {
  //   this.store.dispatch(GetFavouriteItemAction({params}));
  // }

  public getFavoriteItems(itemType: string): Observable<any[]> {
    return this.store.select(selectFavoriteItemsByType(itemType));
  }

  public getFavoriteLocations() {
    this.store.dispatch(GetFavouriteLocationsAction());
  }

  public getFavoriteVouchers() {
    this.store.dispatch(GetFavouriteVouchersAction());
  }

  public getFavoriteWallets() {
    this.store.dispatch(GetFavouriteWalletsAction());
  }

  public isLimitReached(itemType: string) {
    return this.store.select(selectLimitReachedByType(itemType));
  }

  public addItemToFavorites(itemType: string, item: any) {
    this.store.dispatch(AddFavouriteItemAction({ itemType, item }));
  }

  public removeItemFromFavorites(itemType: string, itemId: number | string) {
    this.store.dispatch(RemoveFavouriteItemAction({ itemType, itemId }));
  }

  public clearState(actionType: string): void {
    this.store.dispatch(ClearStateAction({ actionType }));
  }

  public isItemFavorite(itemType: string, item: any): Observable<boolean> {
    const itemId = getItemId(itemType, item);
    return this.getFavoriteItems(itemType).pipe(
      map((favoriteItems) => favoriteItems.some((favItem) => getItemId(itemType, favItem) === itemId))
    );
  }

  public getIconState(itemType: string, item: any): Observable<{ isFavorite: boolean; limitReached: boolean }> {
    if (!item) {
      return of({ isFavorite: false, limitReached: false });
    }

    return combineLatest([this.isItemFavorite(itemType, item), this.isLimitReached(itemType)]).pipe(
      map(([isFavorite, limitReached]) => ({
        isFavorite,
        limitReached,
      })),
      catchError(() => of({ isFavorite: false, limitReached: false })) // Ensure that any error doesn't break the logic
    );
  }

  public setAccountID(accountID: number): void {
    this.store.dispatch(setAccountID({ accountID }));
  }

  public clearFavoriteItems() {
    this.store.dispatch(ClearFavoriteItemsAction());
  }
}
